import { z } from "zod";
import { getPhoneValidationMessage, type CountryCode } from "@/utils/phone";

export const ProfessionalType = {
  COUNSELING_PSYCHOLOGIST: "COUNSELING_PSYCHOLOGIST",
  COUNSELLOR: "COUNSELLOR",
  REGISTERED_CLINICAL_PSYCHOLOGIST: "REGISTERED_CLINICAL_PSYCHOLOGIST",
  REGISTERED_CLINICAL_PSYCHOLOGIST_ASSOCIATE:
    "REGISTERED_CLINICAL_PSYCHOLOGIST_ASSOCIATE",
  PSYCHIATRIST_MD: "PSYCHIATRIST_MD",
  PSYCHIATRIST_DNB: "PSYCHIATRIST_DNB",
  OBGYN_MS: "OBGYN_MS",
  SURGEON_MS: "SURGEON_MS",
  GENERAL_PHYSICIAN: "GENERAL_PHYSICIAN",
} as const;

export type ProfessionalType =
  (typeof ProfessionalType)[keyof typeof ProfessionalType];

export const ProfessionalStatus = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  DISMISSED_TAKING_OLD_PATIENT: "DISMISSED_TAKING_OLD_PATIENT",
  FULLY_DISMISSED: "FULLY_DISMISSED",
  TEMPORARY_INACTIVE: "TEMPORARY_INACTIVE",
} as const;

export type ProfessionalStatus =
  (typeof ProfessionalStatus)[keyof typeof ProfessionalStatus];

export const JobCommitment = {
  PART_TIME: "PART_TIME",
  FULL_TIME: "FULL_TIME",
  FREELANCER: "FREELANCER",
} as const;

export type JobCommitment = (typeof JobCommitment)[keyof typeof JobCommitment];

// helper object for human-readable enum labels
export const professionalTypeLabels: Record<ProfessionalType, string> = {
  [ProfessionalType.COUNSELING_PSYCHOLOGIST]: "Counseling Psychologist",
  [ProfessionalType.COUNSELLOR]: "Counsellor",
  [ProfessionalType.REGISTERED_CLINICAL_PSYCHOLOGIST]:
    "RCI Registered Clinical Psychologist",
  [ProfessionalType.REGISTERED_CLINICAL_PSYCHOLOGIST_ASSOCIATE]:
    "Registered Clinical Psychologist Associate",
  [ProfessionalType.PSYCHIATRIST_MD]: "Psychiatrist MD",
  [ProfessionalType.PSYCHIATRIST_DNB]: "Psychiatrist DNB",
  [ProfessionalType.OBGYN_MS]: "OBGYN MS",
  [ProfessionalType.SURGEON_MS]: "Surgeon MS",
  [ProfessionalType.GENERAL_PHYSICIAN]: "General Physician",
};

export const professionalStatusLabels: Record<ProfessionalStatus, string> = {
  [ProfessionalStatus.ACTIVE]: "Active",
  [ProfessionalStatus.INACTIVE]: "Inactive",
  [ProfessionalStatus.DISMISSED_TAKING_OLD_PATIENT]:
    "Dismissed (Taking Old Patients)",
  [ProfessionalStatus.FULLY_DISMISSED]: "Fully Dismissed",
  [ProfessionalStatus.TEMPORARY_INACTIVE]: "Temporarily Inactive",
};

// Why a professional's status changed (RH-506). Mirrors the server enum
// ProfessionalStatusChangeReason; the server is the source of truth.
export const ProfessionalStatusChangeReason = {
  LONG_LEAVE_START: "LONG_LEAVE_START",
  LONG_LEAVE_RETURN: "LONG_LEAVE_RETURN",
  PIP_START: "PIP_START",
  PIP_COMPLETED: "PIP_COMPLETED",
  DISMISSED: "DISMISSED",
  ON_REQUEST: "ON_REQUEST",
  LOW_SLOT_AVAILABILITY: "LOW_SLOT_AVAILABILITY",
} as const;

export type ProfessionalStatusChangeReason =
  (typeof ProfessionalStatusChangeReason)[keyof typeof ProfessionalStatusChangeReason];

export const professionalStatusChangeReasonLabels: Record<
  ProfessionalStatusChangeReason,
  string
> = {
  [ProfessionalStatusChangeReason.LONG_LEAVE_START]: "Long Leave - Start",
  [ProfessionalStatusChangeReason.LONG_LEAVE_RETURN]: "Long Leave - Return",
  [ProfessionalStatusChangeReason.PIP_START]: "Under PIP - Start",
  [ProfessionalStatusChangeReason.PIP_COMPLETED]: "Under PIP - Completed",
  [ProfessionalStatusChangeReason.DISMISSED]: "Dismissed",
  [ProfessionalStatusChangeReason.ON_REQUEST]: "On Request",
  [ProfessionalStatusChangeReason.LOW_SLOT_AVAILABILITY]:
    "Low Slot Availability",
};

// Which reasons apply when moving INTO a status. Mirrors the server's
// ALLOWED_STATUS_CHANGE_REASONS; the server rejects anything else.
export const allowedStatusChangeReasons: Record<
  ProfessionalStatus,
  ProfessionalStatusChangeReason[]
> = {
  [ProfessionalStatus.ACTIVE]: [
    ProfessionalStatusChangeReason.LONG_LEAVE_RETURN,
    ProfessionalStatusChangeReason.PIP_COMPLETED,
    ProfessionalStatusChangeReason.ON_REQUEST,
  ],
  [ProfessionalStatus.INACTIVE]: [
    ProfessionalStatusChangeReason.LONG_LEAVE_START,
    ProfessionalStatusChangeReason.PIP_START,
    ProfessionalStatusChangeReason.ON_REQUEST,
    ProfessionalStatusChangeReason.LOW_SLOT_AVAILABILITY,
  ],
  [ProfessionalStatus.TEMPORARY_INACTIVE]: [
    ProfessionalStatusChangeReason.LONG_LEAVE_START,
    ProfessionalStatusChangeReason.PIP_START,
    ProfessionalStatusChangeReason.ON_REQUEST,
    ProfessionalStatusChangeReason.LOW_SLOT_AVAILABILITY,
  ],
  [ProfessionalStatus.DISMISSED_TAKING_OLD_PATIENT]: [
    ProfessionalStatusChangeReason.DISMISSED,
    ProfessionalStatusChangeReason.ON_REQUEST,
  ],
  [ProfessionalStatus.FULLY_DISMISSED]: [
    ProfessionalStatusChangeReason.DISMISSED,
    ProfessionalStatusChangeReason.ON_REQUEST,
  ],
};

export const jobCommitmentLabels: Record<JobCommitment, string> = {
  [JobCommitment.FULL_TIME]: "Full Time",
  [JobCommitment.PART_TIME]: "Part Time",
  [JobCommitment.FREELANCER]: "Freelancer",
};

// Phone fields are a country dropdown plus a digits-only number
// (PhoneNumberInput); the pair is combined into one international string,
// e.g. +918291638739, on submit — the format the server validates (RH-856).
// Per-country validity is checked in the superRefine at the bottom.
const countryIso = z.string().length(2);

// Helper function to validate professional date of birth
const validateProfessionalDOB = (dateString: string | null): boolean => {
  if (!dateString) return true; // Allow null (handled by nullable())
  
  try {
    const birthDate = new Date(dateString);
    const today = new Date();
    
    // Check if date is valid
    if (isNaN(birthDate.getTime())) return false;
    
    // Check if date is in the future
    if (birthDate > today) return false;
    
    // Calculate age
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    // Check minimum age (21) and maximum age (100)
    return age >= 21 && age <= 100;
  } catch {
    return false;
  }
};

export const professionalSchema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum(Object.values(ProfessionalType) as [string, ...string[]]),
  sex: z.string().min(1, "Sex is required"),
  date_of_birth: z
    .string()
    .nullable()
    .refine(
      (val) => validateProfessionalDOB(val),
      {
        message: "Professional must be between 21 and 100 years old. Date of birth cannot be in the future.",
      }
    ),
  email: z.string().email().nullable(),
  phone_country_iso: countryIso,
  phone_number: z.string().nullable(),
  registration_number: z.string().nullable(),
  years_of_experience: z.number().int().min(0).max(100).nullable(),
  personal_email: z.string().email().nullable(),
  address: z.string().nullable(),
  area_of_expertise: z.array(z.string()).default([]),
  associated_teams: z.array(z.string()).default([]),
  department: z.string().min(1, "Department is required"),
  languages: z.array(z.string()).min(1, "At least one language is required"),
  supervisor_id: z.string().nullable(),
  lead_ids: z.array(z.string()).default([]),
  age_range_minor: z.array(z.number().int()).max(2).nullable(),
  age_range_adult: z.array(z.number().int()).max(2).nullable(),
  bio: z.string().nullable(),
  country: z.string().nullable(),
  has_kid: z.boolean().nullable(),
  is_lead: z.boolean().nullable(),
  is_married: z.boolean().nullable(),
  is_supervisor: z.boolean().nullable(),
  qualification: z.array(z.string()).default([]),
  specialization: z.string().nullable(),
  university_name: z.string().nullable(),
  preferred_notification_country_iso: countryIso,
  preferred_notification_number: z.string().nullable(),
  pronouns: z.string().nullable(),
  picture: z.instanceof(File).nullable(),
  signature: z.instanceof(File).nullable(),
  stamp: z.instanceof(File).nullable(),
  stamp_and_signature: z.instanceof(File).nullable(),
  professional_image: z.instanceof(File).nullable(),
  job_commitment: z
    .enum(Object.values(JobCommitment) as [string, ...string[]])
    .nullable(),
}).superRefine((data, ctx) => {
  const phones: [
    string | null,
    string,
    "phone_number" | "preferred_notification_number",
  ][] = [
    [data.phone_number, data.phone_country_iso, "phone_number"],
    [
      data.preferred_notification_number,
      data.preferred_notification_country_iso,
      "preferred_notification_number",
    ],
  ];
  for (const [national, iso, path] of phones) {
    if (!national) continue;
    const message = getPhoneValidationMessage(national, iso as CountryCode);
    if (message) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });
    }
  }
});

export type ProfessionalFormData = z.infer<typeof professionalSchema>;
